import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type {
  TodoListCollaboratorResponse,
  TodoItemAssignmentResponse,
  TodoSectionAssignmentResponse,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface UseTodoCollaboratorsReturn {
  addCollaborator: (input: {
    username: string;
    role?: string;
  }) => Promise<TodoListCollaboratorResponse>;
  updateCollaborator: (
    userId: string,
    input: { role: string },
  ) => Promise<TodoListCollaboratorResponse>;
  removeCollaborator: (userId: string) => Promise<void>;
  assignItem: (itemId: string, input: { username: string }) => Promise<TodoItemAssignmentResponse>;
  unassignItem: (itemId: string, userId: string) => Promise<void>;
  assignSection: (
    sectionId: string,
    input: { username: string },
  ) => Promise<TodoSectionAssignmentResponse>;
  unassignSection: (sectionId: string, userId: string) => Promise<void>;
}

export function useTodoCollaborators(listId: string): UseTodoCollaboratorsReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);
  const { toast } = useToast();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: detailKey });
  };

  const onError = () => {
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };

  const addMut = useMutation({
    mutationFn: (input: { username: string; role?: string }) =>
      api.addTodoCollaborator(listId, input),
    onSuccess,
    onError,
  });

  const updateMut = useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: { role: string } }) =>
      api.updateTodoCollaborator(listId, userId, input),
    onSuccess,
    onError,
  });

  const removeMut = useMutation({
    mutationFn: (userId: string) => api.removeTodoCollaborator(listId, userId),
    onSuccess,
    onError,
  });

  const assignItemMut = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: { username: string } }) =>
      api.assignTodoItem(listId, itemId, input),
    onSuccess,
    onError,
  });

  const unassignItemMut = useMutation({
    mutationFn: ({ itemId, userId }: { itemId: string; userId: string }) =>
      api.unassignTodoItem(listId, itemId, userId),
    onSuccess,
    onError,
  });

  const assignSectionMut = useMutation({
    mutationFn: ({ sectionId, input }: { sectionId: string; input: { username: string } }) =>
      api.assignTodoSection(listId, sectionId, input),
    onSuccess,
    onError,
  });

  const unassignSectionMut = useMutation({
    mutationFn: ({ sectionId, userId }: { sectionId: string; userId: string }) =>
      api.unassignTodoSection(listId, sectionId, userId),
    onSuccess,
    onError,
  });

  return {
    addCollaborator: (input) => addMut.mutateAsync(input),
    updateCollaborator: (userId, input) => updateMut.mutateAsync({ userId, input }),
    removeCollaborator: (userId) => removeMut.mutateAsync(userId),
    assignItem: (itemId, input) => assignItemMut.mutateAsync({ itemId, input }),
    unassignItem: (itemId, userId) => unassignItemMut.mutateAsync({ itemId, userId }),
    assignSection: (sectionId, input) => assignSectionMut.mutateAsync({ sectionId, input }),
    unassignSection: (sectionId, userId) => unassignSectionMut.mutateAsync({ sectionId, userId }),
  };
}
